import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { logError } from '../lib/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Convert URL-safe base64 (VAPID public key) to Uint8Array for applicationServerKey
const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) output[i] = raw.charCodeAt(i);
  return output;
};

const isSupported = () =>
  typeof window !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window;

// iOS Safari requires the app to be installed as a standalone PWA to receive push
const isIOS = () =>
  typeof navigator !== 'undefined' &&
  /iPad|iPhone|iPod/.test(navigator.userAgent) &&
  !window.MSStream;

const isStandalonePWA = () =>
  typeof window !== 'undefined' &&
  (window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator.standalone === true);

export const usePushNotifications = () => {
  const [supported] = useState(isSupported);
  const [permission, setPermission] = useState(supported ? Notification.permission : 'unsupported');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Check existing subscription on mount
  useEffect(() => {
    if (!supported) return;
    (async () => {
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        setIsSubscribed(!!sub);
      } catch (err) {
        logError('usePushNotifications:check', err);
      }
    })();
  }, [supported]);

  const subscribe = useCallback(async () => {
    if (!supported) {
      setError('Il tuo browser non supporta le notifiche push.');
      return false;
    }

    // iOS blocks push in Safari browser — only the installed PWA works
    if (isIOS() && !isStandalonePWA()) {
      setError(
        'Su iPhone le notifiche arrivano solo se l\'app è installata: tocca "Condividi" in Safari e poi "Aggiungi a Home". Apri poi l\'app dall\'icona sulla home.'
      );
      return false;
    }

    setLoading(true);
    setError(null);
    try {
      // Ask permission if needed
      let perm = Notification.permission;
      if (perm === 'default') {
        perm = await Notification.requestPermission();
        setPermission(perm);
      }
      if (perm === 'denied') {
        setError('Notifiche bloccate. Vai nelle impostazioni del dispositivo per riattivarle.');
        setLoading(false);
        return false;
      }
      if (perm !== 'granted') {
        setLoading(false);
        return false;
      }

      const reg = await navigator.serviceWorker.ready;

      // Fetch VAPID public key from backend (public endpoint)
      const { data } = await axios.get(`${API}/push/vapid-key`);
      if (!data?.publicKey) throw new Error('Chiave VAPID mancante lato server');

      // Subscribe
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(data.publicKey),
        });
      }

      // Send subscription to backend (auth via cookie)
      const json = sub.toJSON();
      await axios.post(`${API}/push/subscribe`, { endpoint: json.endpoint, keys: json.keys });

      setIsSubscribed(true);
      return true;
    } catch (err) {
      logError('usePushNotifications:subscribe', err);
      // Surface a human-readable reason so the user can act (common on iOS: InvalidStateError / NotAllowedError)
      const msg =
        err?.message?.includes('standalone') || err?.name === 'NotAllowedError'
          ? 'Permesso negato. Su iPhone apri l\'app dall\'icona sulla Home (non da Safari) e riprova.'
          : err?.message || 'Impossibile attivare le notifiche. Riprova.';
      setError(msg);
      return false;
    } finally {
      setLoading(false);
    }
  }, [supported]);

  const unsubscribe = useCallback(async () => {
    if (!supported) return false;
    setLoading(true);
    setError(null);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await sub.unsubscribe();
        // Tell backend to drop the stored subscription too (best-effort)
        try {
          await axios.post(`${API}/push/unsubscribe`, { endpoint: sub.endpoint });
        } catch (e) { logError('usePushNotifications:unsubscribe-backend', e); }
      }
      setIsSubscribed(false);
      return true;
    } catch (err) {
      logError('usePushNotifications:unsubscribe', err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [supported]);

  return { supported, permission, isSubscribed, loading, error, subscribe, unsubscribe };
};
